import React from "react";
import { Drawer, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAddAlt1";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ForkRightIcon from "@mui/icons-material/ForkRight";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ListIcon from "@mui/icons-material/List";
import SettingsIcon from "@mui/icons-material/Settings";
import ViewStreamIcon from "@mui/icons-material/ViewStream";
import FactoryIcon from "@mui/icons-material/Factory";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import HubIcon from "@mui/icons-material/Hub";
import WebIcon from "@mui/icons-material/Web";
import ApartmentIcon from "@mui/icons-material/Apartment";
import AssessmentIcon from "@mui/icons-material/Assessment";
import RuleIcon from "@mui/icons-material/Rule";
import KeyIcon from "@mui/icons-material/Key";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import HistoryIcon from "@mui/icons-material/History";
import { useAuth } from "../../providers/AuthProvider";
import { hasAnyGrant } from "../../services/shared";
import { SidebarItem } from "./SidebarItem";
import { SidebarSection } from "./SidebarSection";
import { SidebarCollapsible } from "./SidebarCollapsible";
import logo from "../../assets/logo_affida.png";
import style from "./Sidebar.module.css";

interface Props {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

function SidebarNav({ onItemClick }: { onItemClick?: () => void }) {
  const { grants } = useAuth();

  const hasDashboard = hasAnyGrant(grants, ["admin", "quality_op", "fornitore_admin"]);
  const hasLpList = hasAnyGrant(grants, ["admin", "lp_standalone"]);
  const hasSmist = hasAnyGrant(grants, ["admin", "dispatcher_admin"]);
  const hasOrg = hasAnyGrant(grants, ["admin"]);
  const hasChatbot = hasAnyGrant(grants, ["admin", "chatbot_admin"]);
  const hasEgg = hasAnyGrant(grants, ["admin"]);
  const hasLeadDispatch = hasAnyGrant(grants, ["admin", "dispatcher_admin"]);
  const isAdmin = hasAnyGrant(grants, ["admin"]);
  const hasCreateUser = hasAnyGrant(grants, ["admin", "user_admin"]);
  const hasLpAgents = hasAnyGrant(grants, ["admin", "lp_admin", "lp_builder"]);
  const hasGestioneSection =
    hasSmist || hasOrg || hasChatbot || hasLpAgents || hasEgg || hasLeadDispatch;

  return (
    <>
      <div className={style.sidebarBody}>
        <SidebarSection>
          {hasDashboard && (
            <SidebarItem
              path="/dashboard"
              label="Dashboard"
              icon={DashboardIcon}
              onClick={onItemClick}
            />
          )}
          <SidebarItem
            path="/lead-list"
            label="Lista lead"
            icon={ListIcon}
            onClick={onItemClick}
          />
          <SidebarItem
            path="/lead-collect"
            label="Aggiungi lead"
            icon={PersonAddIcon}
            onClick={onItemClick}
          />
          {hasLpList && (
            <SidebarItem
              path="/lp-list"
              label="Lista landing"
              icon={ViewStreamIcon}
              onClick={onItemClick}
            />
          )}
        </SidebarSection>

        {hasGestioneSection && (
          <SidebarSection label="Gestione">
            {hasSmist && (
              <SidebarItem
                path="/smist"
                label="Regole smistamento"
                icon={ForkRightIcon}
                onClick={onItemClick}
              />
            )}
            {hasOrg && (
              <SidebarItem
                path="/organization-details"
                label="Organizzazioni"
                icon={FactoryIcon}
                onClick={onItemClick}
              />
            )}
            {hasLpAgents && (
              <SidebarItem
                path="/lp-agents"
                label="Landing Page Agenti"
                icon={WebIcon}
                onClick={onItemClick}
              />
            )}
            {hasEgg && (
              <SidebarCollapsible
                label="EGG Gateway"
                icon={HubIcon}
                basePath="/egg"
              >
                <SidebarItem
                  path="/egg/rules"
                  label="Regole"
                  icon={RuleIcon}
                  onClick={onItemClick}
                  indent
                />
                <SidebarItem
                  path="/egg/webhook-keys"
                  label="Webhook Keys"
                  icon={KeyIcon}
                  onClick={onItemClick}
                  indent
                />
              </SidebarCollapsible>
            )}
            {hasLeadDispatch && (
              <SidebarCollapsible
                label="Lead Dispatching"
                icon={CallSplitIcon}
                basePath="/lead-dispatch"
              >
                <SidebarItem
                  path="/lead-dispatch/rules"
                  label="Regole"
                  icon={RuleIcon}
                  onClick={onItemClick}
                  indent
                />
                {isAdmin && (
                  <SidebarItem
                    path="/lead-dispatch/logs"
                    label="Log globali"
                    icon={HistoryIcon}
                    onClick={onItemClick}
                    indent
                  />
                )}
              </SidebarCollapsible>
            )}
            {hasChatbot && (
              <SidebarCollapsible
                label="Chatbot"
                icon={SmartToyIcon}
                basePath="/chatbot"
              >
                <SidebarItem
                  path="/chatbot/tenants"
                  label="Tenant"
                  icon={ApartmentIcon}
                  onClick={onItemClick}
                  indent
                />
                <SidebarItem
                  path="/chatbot/chatbots"
                  label="Chatbot"
                  icon={SmartToyIcon}
                  onClick={onItemClick}
                  indent
                />
                <SidebarItem
                    path="/chatbot/flows"
                    label="Flows"
                    icon={AccountTreeIcon}
                    onClick={onItemClick}
                    indent
                />
                <SidebarItem
                  path="/chatbot/monitoring"
                  label="Monitoraggio"
                  icon={AssessmentIcon}
                  onClick={onItemClick}
                  indent
                />
              </SidebarCollapsible>
            )}
          </SidebarSection>
        )}

        {hasCreateUser && (
          <SidebarSection label="Admin">
            <SidebarItem
              path="/create-user"
              label="Crea utente"
              icon={SettingsIcon}
              onClick={onItemClick}
            />
          </SidebarSection>
        )}
      </div>

      <div className={style.sidebarBottom}>
        <SidebarItem
          path="/user-profile"
          label="Profilo utente"
          icon={AccountCircleIcon}
          onClick={onItemClick}
        />
      </div>
    </>
  );
}

export default function Sidebar({ mobileOpen, onMobileClose }: Props) {
  return (
    <>
      {/* Desktop + Tablet sidebar */}
      <aside className={style.sidebarContainer}>
        <div className={style.sidebarBrand}>
          <img src={logo} alt="Logo" className={style.sidebarBrandLogo} />
        </div>
        <SidebarNav />
      </aside>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
      >
        <div className={style.drawerContent}>
          <div className={style.drawerHeader}>
            <div className={style.drawerHeaderBrand}>
              <img src={logo} alt="Logo" className={style.sidebarBrandLogo} />
            </div>
            <IconButton onClick={onMobileClose} size="small" aria-label="Chiudi menu">
              <CloseIcon />
            </IconButton>
          </div>
          <div className={style.drawerBody}>
            <SidebarNav onItemClick={onMobileClose} />
          </div>
        </div>
      </Drawer>
    </>
  );
}
